import { mapValues } from 'es-toolkit';
import React, { useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, Dialog, IconButton, LinearProgress, Toolbar, Typography } from '@mui/material';

import { closeCurrentPopup } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';

import { getAssemblyMemberParts, getAssemblyPlateParts } from '../../store/selectors/assemblyPartsUtilities';
import { getActiveStation } from '../../store/selectors/getActiveStation';
import { getComponentInventoryStockLengths } from '../../store/selectors/getComponentInventoryStockLengths';
import { useAppDispatch, useAppSelector } from '../hooks';
import { AssemblyMemberPartDiagrams } from '../interface/panels/assembly-parts/AssemblyMemberPartDiagrams';
import { AssemblyPlateParts } from '../interface/panels/assembly-parts/AssemblyPlateParts';
import { PartsFilterSelection } from '../interface/panels/assembly-parts/getMemberFilter';
import { getDefaultPartsFilterSelection, PartsFilter } from '../interface/panels/assembly-parts/PartsFilter';

export function StationPartsDialog() {
  const dispatch = useAppDispatch();
  const activeStation = useAppSelector(getActiveStation);
  const componentDesignsByGuid = useAppSelector((state) => state.componentDesigns.data);
  const componentInventoryStockLengths = useAppSelector(getComponentInventoryStockLengths);

  const componentDesignGuidToQuantity = useMemo(
    () => (activeStation ? mapValues(activeStation.componentDesigns, ({ quantity }) => quantity ?? 1) : {}),
    [activeStation],
  );
  const assemblyMemberParts = useMemo(
    () =>
      activeStation
        ? getAssemblyMemberParts(
            componentDesignGuidToQuantity,
            componentDesignsByGuid,
            componentInventoryStockLengths,
            'desc',
          )
        : [],
    [activeStation, componentDesignGuidToQuantity, componentDesignsByGuid, componentInventoryStockLengths],
  );
  const assemblyPlateParts = useMemo(
    () => (activeStation ? getAssemblyPlateParts(componentDesignGuidToQuantity, componentDesignsByGuid, false) : []),
    [activeStation, componentDesignGuidToQuantity, componentDesignsByGuid],
  );

  const [partsFilterSelection, setPartsFilterSelection] = useState<PartsFilterSelection>(
    getDefaultPartsFilterSelection(activeStation),
  );

  if (activeStation == null || activeStation.followsSchedule) {
    return null;
  }

  return (
    <Dialog fullScreen open sx={createScrollbarStyling}>
      <AppBar sx={{ position: 'relative', mb: 2 }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => void dispatch(closeCurrentPopup())}>
            <CloseIcon />
          </IconButton>

          <Typography sx={{ mx: 2 }} variant="h6">
            Parts for {activeStation.name}
          </Typography>

          <Box sx={{ my: 1, ml: 1 }}>
            <PartsFilter
              partsFilterSelection={partsFilterSelection}
              setPartsFilterSelection={setPartsFilterSelection}
            />
          </Box>
        </Toolbar>
        {Object.keys(activeStation.componentDesigns).some(
          (componentDesignGuid) => !(componentDesignGuid in componentDesignsByGuid),
        ) && <LinearProgress color="secondary" />}
      </AppBar>
      <Box sx={{ height: '100%', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', overflow: 'auto', mb: 8 }}>
          {partsFilterSelection === 'plates' ? (
            <AssemblyPlateParts assemblyPlateParts={assemblyPlateParts} />
          ) : (
            <AssemblyMemberPartDiagrams
              assemblyMemberParts={assemblyMemberParts}
              partsFilterSelection={partsFilterSelection}
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
